import m from 'mithril';
import { Store } from '../store';
import { ApiService } from '../services/api';
import { Button } from '../components/Buttons';
import { t } from '../i18n';

export const CheckoutPage: m.Component = {
    oninit() {
        if (Store.state.cart.length === 0) {
            m.route.set('/cart');
            return;
        }
        // Allowed to be guest
        // if (!Store.state.user) {
        //     Store.addToast('Please sign in to complete checkout', 'info');
        //     Store.state.isAuthModalOpen = true;
        //     m.route.set('/cart');
        //     return;
        // }
    },
    view() {
        return m('div', { class: 'max-w-3xl mx-auto px-4 sm:px-6 pt-24 md:pt-32 pb-20 animate-fade-in text-[var(--color-text-base)] min-h-[100dvh]' }, [
            
            // Header
            m('div', { class: 'mb-8 md:mb-10 text-center' }, [
                m('h1', { class: 'text-3xl md:text-5xl font-black tracking-tight' }, 'Secure Checkout'),
                m('p', { class: 'opacity-60 font-medium mt-3 text-sm max-w-md mx-auto' }, 'You are just a few steps away from completing your order.')
            ]),

            state.isSuccess ? renderSuccessState() : renderCheckoutForm()
        ]);
    }
};

const state = {
    email: '',
    address: '',
    city: '',
    zip: '',
    paymentMethod: 'credit_card',
    isProcessing: false,
    paymentProcessingText: 'Processing Order...',
    isSuccess: false,
    promoCode: '',
    discountApplied: false
};

function renderCheckoutForm() {
    const subtotal = Store.state.cart.reduce((acc: number, item: any) => acc + (Number(item.price) * (item.quantity || 1)), 0);
    const shipping = 15.00;
    const discountAmount = state.discountApplied ? subtotal * 0.10 : 0;
    const total = subtotal + shipping - discountAmount;

    return m('div', { class: 'grid md:grid-cols-2 gap-8 md:gap-12 relative' }, [
        
        // Left Column: Form
        m('form', {
            class: 'space-y-6',
            onsubmit: async (e: Event) => {
                e.preventDefault();
                if (!state.address || !state.city || !state.zip || (!Store.state.user && !state.email)) {
                    Store.addToast('Please fill out all required fields', 'error');
                    return;
                }
                
                state.isProcessing = true;
                const fullAddress = `${state.address}, ${state.city}, ${state.zip}`;
                
                try {
                    // Auto-register guest
                    if (!Store.state.user) {
                        try {
                            const password = Math.random().toString(36).slice(-8) + 'A1!';
                            const res = await ApiService.register({ name: 'Guest User', email: state.email, password });
                            if (res.success && res.user) {
                                Store.state.user = res.user;
                            } else {
                                throw new Error('Email may already be in use');
                            }
                        } catch (err: any) {
                            Store.addToast('Email already in use. Please sign in.', 'error');
                            Store.state.isAuthModalOpen = true;
                            state.isProcessing = false;
                            m.redraw();
                            return;
                        }
                    }

                    // 1. Sync local cart directly to DB 
                    const cartPayload = Store.state.cart.map((item: any) => ({
                        productId: item.product?.id || item.id,
                        quantity: item.quantity || 1
                    }));
                    await ApiService.syncCart(cartPayload);

                    // 2. Trigger Checkout (Backend handles placing the order and deducting inventory)
                    await ApiService.checkout(fullAddress, state.paymentMethod);
                    
                    // Simulate a secure payment processor delay
                    state.paymentProcessingText = 'Connecting to secure payment gateway...';
                    m.redraw();
                    await new Promise(r => setTimeout(r, 800));
                    state.paymentProcessingText = 'Verifying payment details...';
                    m.redraw();
                    await new Promise(r => setTimeout(r, 800));
                    state.paymentProcessingText = 'Authorizing transaction...';
                    m.redraw();
                    await new Promise(r => setTimeout(r, 800));
                    
                    // 3. Success cleanup
                    Store.state.cart = [];
                    state.isSuccess = true;
                    // Jump to top of screen smoothly
                    window.scrollTo({ top: 0, behavior: 'smooth' });

                } catch (err: any) {
                    Store.addToast(err.message || 'Checkout failed', 'error');
                } finally {
                    state.isProcessing = false;
                    m.redraw();
                }
            }
        }, [
            // Section 1: Shipping
            m('div', { class: 'bg-[var(--color-surface)] p-6 md:p-8 rounded-3xl border border-[var(--color-border)] shadow-sm' }, [
                m('div', { class: 'flex items-center gap-3 mb-6' }, [
                    m('div', { class: 'w-10 h-10 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] flex items-center justify-center font-black' }, '1'),
                    m('h2', { class: 'text-xl font-bold' }, 'Shipping details')
                ]),
                m('div', { class: 'space-y-4' }, [
                    !Store.state.user && createInput('Email Address', 'email', state.email, v => state.email = v, 'guest@example.com'),
                    createInput('Street Address', 'text', state.address, v => state.address = v, '123 Main St'),
                    m('div', { class: 'grid grid-cols-2 gap-4' }, [
                        createInput('City', 'text', state.city, v => state.city = v, 'New York'),
                        createInput('Postal Code', 'text', state.zip, v => state.zip = v, '10001'),
                    ])
                ])
            ]),

            // Section 2: Payment
            m('div', { class: 'bg-[var(--color-surface)] p-6 md:p-8 rounded-3xl border border-[var(--color-border)] shadow-sm' }, [
                m('div', { class: 'flex items-center gap-3 mb-6' }, [
                    m('div', { class: 'w-10 h-10 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] flex items-center justify-center font-black' }, '2'),
                    m('h2', { class: 'text-xl font-bold' }, 'Payment method')
                ]),
                m('div', { class: 'space-y-3' }, [
                    createRadioOption('Credit Card', 'credit_card', 'lucide:credit-card'),
                    createRadioOption('PayPal', 'paypal', 'lucide:coins'),
                    createRadioOption('Cash on Delivery', 'cash', 'lucide:banknote')
                ])
            ])
        ]),

        // Right Column: Summary
        m('div', { class: 'md:relative' }, [
            m('div', { class: 'bg-[var(--color-surface)] p-6 md:p-8 rounded-3xl border border-[var(--color-border)] shadow-sm sticky top-24' }, [
                m('h2', { class: 'text-xl font-bold mb-6 border-b border-[var(--color-border)] pb-4' }, 'Order Summary'),
                
                // Item Preview Mini-list
                m('div', { class: 'space-y-4 mb-6 max-h-60 overflow-y-auto pe-2 custom-scrollbar' }, 
                    Store.state.cart.map((item: any) => 
                        m('div', { class: 'flex items-center gap-3' }, [
                            m('img', { src: item.images?.[0] || 'https://via.placeholder.com/100', class: 'w-12 h-12 rounded-lg object-cover' }),
                            m('div', { class: 'flex-1 min-w-0' }, [
                                m('p', { class: 'text-sm font-bold truncate' }, item.name),
                                m('p', { class: 'text-xs opacity-60' }, `Qty: ${item.quantity || 1}`)
                            ]),
                            m('p', { class: 'text-sm font-bold text-[var(--color-primary)]' }, `$${Number(item.price).toFixed(2)}`)
                        ])
                    )
                ),

                m('div', { class: 'space-y-3 opacity-70 mb-4' }, [
                    m('div', { class: 'flex justify-between text-sm' }, [
                        m('span', 'Subtotal'),
                        m('span', { class: 'font-bold' }, `$${subtotal.toFixed(2)}`)
                    ]),
                    m('div', { class: 'flex justify-between text-sm' }, [
                        m('span', 'Shipping'),
                        m('span', { class: 'font-bold' }, `$${shipping.toFixed(2)}`)
                    ]),
                    state.discountApplied ? m('div', { class: 'flex justify-between text-sm text-[var(--color-primary)] font-bold' }, [
                        m('span', 'Discount (10%)'),
                        m('span', `-$${discountAmount.toFixed(2)}`)
                    ]) : null
                ]),
                
                // Promo Code Input
                m('div', { class: 'mb-6 flex gap-2' }, [
                    m('input', {
                        type: 'text',
                        placeholder: 'Promo code (try KHALID10)',
                        value: state.promoCode,
                        oninput: (e: any) => state.promoCode = e.target.value.toUpperCase(),
                        class: 'flex-1 px-4 py-2 bg-[var(--color-bg-base)] border border-[var(--color-border)] rounded-xl outline-none focus:border-[var(--color-primary)] font-bold text-sm tracking-wide uppercase transition-colors'
                    }),
                    m(Button, { 
                        variant: state.discountApplied ? 'primary' : 'outline', 
                        size: 'md',
                        class: 'font-bold uppercase tracking-widest text-[10px]',
                        onclick: () => {
                            if (state.promoCode === 'KHALID10') {
                                state.discountApplied = true;
                                Store.addToast('10% discount applied!', 'success');
                            } else {
                                state.discountApplied = false;
                                Store.addToast('Invalid promo code', 'error');
                            }
                        }
                    }, state.discountApplied ? 'Applied' : 'Apply')
                ]),

                m('div', { class: 'border-t border-[var(--color-border)] pt-4 mb-8 flex justify-between items-center' }, [
                    m('span', { class: 'text-lg font-bold' }, 'Total'),
                    m('span', { class: 'text-2xl font-black text-[var(--color-primary)]' }, `$${total.toFixed(2)}`)
                ]),

                m('button', {
                    class: `w-full py-4 rounded-xl font-bold tracking-wide text-white transition-all shadow-md flex items-center justify-center gap-2
                            ${state.isProcessing ? 'bg-[var(--color-primary)]/70 cursor-not-allowed' : 'bg-[var(--color-primary)] hover:scale-[1.02] hover:shadow-lg active:scale-95 cursor-pointer'}`,
                    onclick: (e: Event) => {
                        // Submit the closest form (the big one on the left)
                        const form = document.querySelector('form');
                        if (form && !state.isProcessing) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                    },
                    disabled: state.isProcessing
                }, [
                    state.isProcessing && m('iconify-icon', { icon: 'lucide:loader-2', class: 'animate-spin text-xl' }),
                    state.isProcessing ? state.paymentProcessingText : 'Place Order Now'
                ])
            ])
        ])

    ]);
}

function renderSuccessState() {
    return m('div', { class: 'flex flex-col items-center justify-center text-center py-10 md:py-20 animate-fade-in-up' }, [
        m('div', { class: 'w-24 h-24 mb-6 rounded-full bg-green-500/10 flex items-center justify-center text-green-500' }, [
            m('iconify-icon', { icon: 'lucide:check-circle-2', class: 'text-5xl animate-scale-in' })
        ]),
        m('h2', { class: 'text-3xl font-black mb-3' }, 'Order Confirmed!'),
        m('p', { class: 'opacity-70 max-w-md mx-auto mb-10 text-sm md:text-base' }, 'Thank you for your purchase. We have received your order and are currently processing it. You will receive an email confirmation shortly.'),
        
        m('div', { class: 'flex flex-col sm:flex-row gap-4 w-full sm:w-auto justify-center' }, [
            m(Button, { variant: 'outline', size: 'lg', class: 'w-full sm:w-48', onclick: () => m.route.set('/profile', { tab: 'orders' }) }, 'View Orders'),
            m(Button, { variant: 'primary', size: 'lg', class: 'w-full sm:w-48', onclick: () => m.route.set('/') }, 'Continue Shopping')
        ])
    ]);
}

// --- Helpers ---

function createInput(label: string, type: string, value: string, setValue: (v: string) => void, placeholder: string) {
    return m('div', { class: 'space-y-1.5 group' }, [
        m('label', { class: 'text-xs font-black text-[var(--color-text-base)] opacity-70 tracking-widest uppercase ms-1 block transition-all group-focus-within:text-[var(--color-primary)] group-focus-within:opacity-100' }, label),
        m('input', {
            type,
            required: true,
            placeholder,
            value,
            oninput: (e: any) => setValue(e.target.value),
            class: 'w-full px-4 py-3.5 bg-[var(--color-bg-base)] border-2 border-[var(--color-border)] rounded-xl outline-none focus:border-[var(--color-primary)] focus:ring-4 focus:ring-[var(--color-primary-glow)] transition-all duration-300 font-medium hover:border-opacity-50 hover:shadow-md shadow-sm'
        })
    ]);
}

function createRadioOption(label: string, value: string, icon: string) {
    const isSelected = state.paymentMethod === value;
    return m('label', { 
        class: `flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-all
                ${isSelected ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5' : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/30'}`
    }, [
        m('div', { class: 'flex items-center gap-3' }, [
            m('div', { class: `w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-[var(--color-primary)]' : 'border-[var(--color-border)]'}` }, [
                isSelected && m('div', { class: 'w-2.5 h-2.5 rounded-full bg-[var(--color-primary)] animate-scale-in' })
            ]),
            m('iconify-icon', { icon, class: `text-xl ${isSelected ? 'text-[var(--color-primary)]' : 'opacity-50'}` }),
            m('span', { class: `font-bold ${isSelected ? 'text-[var(--color-primary)]' : ''}` }, label)
        ]),
        m('input', {
            type: 'radio',
            name: 'payment',
            value,
            checked: isSelected,
            class: 'hidden',
            onchange: () => state.paymentMethod = value
        })
    ]);
}
